/**
 * Ruler scale for the King of the Mountain boards.
 *
 * Both boards draw the ruler as 8 equal segments, so the top value is divided by
 * 8 to label each tick. Fed a raw score — the Yeti's best month, say 5,670 — that
 * produces eighths of 708.75, which printed as 708 / 1417 / 2126 / 2835.
 *
 * niceAxisMax rounds the TOP of the scale up until every tick lands on a round
 * number (0, 1000, 2000 …). Scores themselves are never rounded; only the scale
 * they are measured against. Position the markers with this same value and the
 * ruler and the climbers stay in agreement.
 */

/** The ruler's fixed geometry: 8 segments, so 9 labelled ticks. */
export const RULER_SEGMENTS = 8;

/** Step sizes we consider "round", scaled by powers of ten. */
const STEP_MULTIPLES = [1, 2, 5, 10];

/**
 * Smallest round top-of-scale at or above `rawMax`, divisible into
 * RULER_SEGMENTS whole steps. Returns 0 for a missing or non-positive max so
 * callers can keep their existing "no scale yet" guards.
 */
export const niceAxisMax = (rawMax) => {
  const max = Number(rawMax);
  if (!Number.isFinite(max) || max <= 0) return 0;

  const rawStep = max / RULER_SEGMENTS;
  // Never step below 1 — a whole-number ruler can't have fractional ticks.
  const magnitude = Math.max(1, 10 ** Math.floor(Math.log10(rawStep)));
  const step =
    STEP_MULTIPLES.map((m) => m * magnitude).find((s) => s >= rawStep - 1e-9) ??
    STEP_MULTIPLES[STEP_MULTIPLES.length - 1] * magnitude;

  return step * RULER_SEGMENTS;
};

export default niceAxisMax;
